import { Input, Select } from "shared/components";
import {
  pastDueOptions,
  yearsOptions,
  creditScoreTypeOptions,
  recordsOptions,
  CriteriaTargetType
} from "../../../constants";
import { SelectImportance } from "./SelectImportance";
import { ApplicantSettings, CoSignerSettings } from "../../../types";
import { FC } from "react";
import { cx } from "shared/utils";
import { ApplicantSettingsErrors } from "types/landlord/verificationTemplate";
import { GroupTypeLabel } from "../../components/GroupTypeLabel";
import { LeastMostLabel } from "./LeastMostLabel";
import { SettingCard } from "../../../../../components/SettingCard";

type Props = {
  criteria?: CoSignerSettings | ApplicantSettings;
  onValueChange: (
    criteria: string,
    key: string,
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => void;
  applyCriteria: boolean;
  readOnly?: boolean;
  applicantSettingsErrors?: ApplicantSettingsErrors | null;
};

export const CreditReportAndScoreCriteria: FC<Props> = ({
  criteria,
  onValueChange,
  applyCriteria,
  readOnly,
  applicantSettingsErrors
}) => {
  const disabled = Boolean(!applyCriteria || readOnly);

  if (!criteria) return null;

  return (
    <SettingCard title="Credit report and score">
      <div className="mb-3">
        <GroupTypeLabel title="Credit score" group className="text-sm" />
      </div>
      <div className="flex justify-between mb-6">
        <div className="flex gap-2">
          <div>
            <Select
              id="credit_score_type"
              options={creditScoreTypeOptions}
              defaultValue={criteria.credit_score?.type || "Average"}
              onChange={(e) => onValueChange("credit_score", "type", e)}
              hideOptional
              isDisabled={disabled}
              searchable={false}
            />
          </div>
          <LeastMostLabel />

          <Input
            id="credit_score"
            type="number"
            noIntlNormalization
            className={cx(
              "max-w-[76px] w-full",
              applicantSettingsErrors?.credit_score
                ? "text-danger-700 border-danger-300"
                : ""
            )}
            required
            value={criteria.credit_score?.value}
            onChange={(e) => onValueChange("credit_score", "value", e)}
            readOnly={disabled}
          />
        </div>
        <SelectImportance
          importance={criteria.credit_score?.importance}
          onChange={(e) => onValueChange("credit_score", "importance", e)}
          disabled={disabled}
        />

        {applicantSettingsErrors?.credit_score ? (
          <p className="text-danger-500 text-xs">
            {applicantSettingsErrors.credit_score}
          </p>
        ) : null}
      </div>

      <p className="mb-3 text-sm font-medium text-content-1000">
        Past due accounts
        <span className="text-content-200">
          {" "}
          ({CriteriaTargetType.INDIVIDUAL})
        </span>
      </p>
      <div className="flex items-end justify-between mb-6">
        <div className="flex gap-2">
          <Select
            id="past_due_account_rate"
            options={pastDueOptions}
            defaultValue={criteria.past_due_account?.past_due_account_rate}
            onChange={(e) =>
              onValueChange("past_due_account", "past_due_account_rate", e)
            }
            hideOptional
            isDisabled={disabled}
            searchable={false}
          />
          <LeastMostLabel most />

          <Input
            id="past_due_account"
            type="number"
            noIntlNormalization
            className={cx(
              "max-w-[76px] w-full",
              applicantSettingsErrors?.past_due_account
                ? "text-danger-700 border-danger-300"
                : ""
            )}
            required
            value={criteria.past_due_account?.value}
            onChange={(e) => onValueChange("past_due_account", "value", e)}
            readOnly={disabled}
          />
        </div>
        <SelectImportance
          importance={criteria.past_due_account?.importance}
          onChange={(e) => onValueChange("past_due_account", "importance", e)}
          disabled={disabled}
        />

        {applicantSettingsErrors?.past_due_account ? (
          <p className="mb-3 text-danger-500 text-xs">
            {applicantSettingsErrors.past_due_account}
          </p>
        ) : null}
      </div>

      <p className="mb-3 text-sm font-medium text-content-1000">
        Balance past due
        <span className="text-content-200">
          {" "}
          ({CriteriaTargetType.INDIVIDUAL})
        </span>
      </p>
      <div className="flex justify-between mb-6">
        <div className="flex gap-2">
          <Input
            id="unpaid_collections_balance"
            required
            value={criteria.unpaid_collections_balance?.value}
            type="money"
            className="max-w-[140px] !mb-0"
            onChange={(e) =>
              onValueChange("unpaid_collections_balance", "value", e)
            }
            error={applicantSettingsErrors?.unpaid_collections_balance}
            readOnly={disabled}
          />
          <LeastMostLabel most />
        </div>

        <SelectImportance
          importance={criteria.unpaid_collections_balance?.importance}
          onChange={(e) =>
            onValueChange("unpaid_collections_balance", "importance", e)
          }
          disabled={disabled}
        />
      </div>

      <p className="mb-3 text-sm font-medium text-content-1000">
        Balance of collections
        <span className="text-content-200">
          {" "}
          ({CriteriaTargetType.INDIVIDUAL})
        </span>
      </p>
      <div className="flex justify-between mb-6">
        <div className="flex gap-2">
          <div>
            <Input
              required
              type="money"
              className="max-w-[140px] !mb-0"
              value={criteria.collections_balance?.value || 0}
              id="collections_balance"
              onChange={(e) => onValueChange("collections_balance", "value", e)}
              readOnly={disabled}
              error={applicantSettingsErrors?.collections_balance}
            />
          </div>
          <LeastMostLabel most />
        </div>
        <SelectImportance
          importance={criteria.collections_balance?.importance}
          onChange={(e) => {
            onValueChange("collections_balance", "importance", e);
          }}
          disabled={disabled}
        />
      </div>

      <p className="mb-3 text-sm font-medium text-content-1000">
        Bankruptcy records
        <span className="text-content-200">
          {" "}
          ({CriteriaTargetType.INDIVIDUAL})
        </span>
      </p>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex gap-2">
          <Select
            id="records"
            options={recordsOptions}
            value={criteria.bankruptcy_records?.value || 0}
            onChange={(e) => onValueChange("bankruptcy_records", "value", e)}
            hideOptional
            isDisabled={disabled}
            searchable={false}
          />
          <LeastMostLabel most />

          <Select
            id="bankruptcy_records_years"
            options={yearsOptions}
            defaultValue={criteria.bankruptcy_records?.years}
            onChange={(e) => onValueChange("bankruptcy_records", "years", e)}
            hideOptional
            isDisabled={disabled}
            searchable={false}
          />
        </div>
        <SelectImportance
          importance={criteria.bankruptcy_records?.importance}
          onChange={(e) => onValueChange("bankruptcy_records", "importance", e)}
          disabled={disabled}
        />
      </div>
    </SettingCard>
  );
};
